import { TFunction } from 'i18next';
import { MembershipTypes, SurveySteps } from './types/enums';

export const schema = (t: TFunction) => ({
  requiredText: '',
  pages: [
    {
      title: t('signup:pages.yourDetails', 'Your details'),
      stepName: SurveySteps.FoundationSignUp,
      elements: [
        {
          type: 'html',
          name: 'explanation',
          html: `<p class="explanation sd-element"> ${t(
            'signup:fields.marketingTextGB',
            'We’re excited for you to join CHAS, UK’s leading compliance and risk management provider for contractors. Fill out the fields below to begin creating your account.'
          )} </p>`
        },
        {
          type: 'mui-text',
          name: 'firstName',
          title: t('signup:fields.firstName', 'First name'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.firstName', 'First name'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 35,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.firstName', 'First name'),
                number: 35
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.firstName', 'First name'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: "^[A-Za-z- ']+$",
              text: t('signup:validation.charSet', {
                defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                field: t('signup:fields.firstName', 'First name'),
                charSet: "a-z A-Z - '"
              })
            }
          ]
        },
        {
          type: 'mui-text',
          name: 'lastName',
          title: t('signup:fields.lastName', 'Last name'),
          startWithNewLine: false,
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.lastName', 'Last name'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 35,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.lastName', 'Last name'),
                number: 35
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.lastName', 'Last name'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: "^[A-Za-z- ']+$",
              text: t('signup:validation.charSet', {
                defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                field: t('signup:fields.lastName', 'Last name'),
                charSet: "a-z A-Z - '"
              })
            }
          ]
        },
        {
          type: 'mui-text',
          name: 'email',
          title: t('signup:fields.emailAddress', 'Email address'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.minLength', {
            defaultValue: '{{field}} should be of minimum {{number}} characters length',
            field: t('signup:fields.emailAddress', 'Email address'),
            number: 2
          }),
          validators: [
            {
              type: 'text',
              maxLength: 80,
              text: t('signup:validation.maxLength', {
                defaultValue: '{{field}} should be of maximum {{number}} characters length',
                field: t('signup:fields.emailAddress', 'Email address'),
                number: 80
              })
            },
            {
              type: 'text',
              minLength: 2,
              text: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.emailAddress', 'Email address'),
                number: 2
              })
            },
            {
              type: 'regex',
              regex: '^(?=.{6,256}$)([+\\w\\.\\-]+)@(([\\w\\-]+\\.)+[\\w\\-]{2,})$',
              text: t(
                'signup:validation.includeValidEmail',
                'Please include valid e-mail box value, example: example@example.com'
              )
            },
            {
              type: 'expression',
              text: t('signup:validation.emailAlreadyExists', 'Email already exists.'),
              expression: 'checkIfEmailExists({email})'
            }
          ]
        },
        {
          type: 'mui-phone-input',
          name: 'phoneNumber',
          title: t('signup:fields.contactPhoneNumber', 'Contact phone number'),
          isRequired: true,
          parentClassName: 'short-input',
          requiredErrorText: t('signup:validation.requirePhoneNumber', 'Phone number is required'),
          validators: [
            {
              type: 'expression',
              expression: 'validatePhoneNumber({phoneNumber})',
              text: t('signup:validation.enterValidPhoneNumber', 'Enter a valid phone number')
            }
          ]
        }
      ],
      stepNumber: 1
    },
    {
      name: 'companyDetailsPanel',
      title: t('signup:pages.companyDetails', 'Company details'),
      stepName: SurveySteps.FoundationSignUp,
      elements: [
        {
          name: 'companyDetailsPanel',
          type: 'panel',
          title: t('signup:pages.companyDetails', 'Company details'),
          description: t('signup:fields.companyAndAddressDetails', 'Enter your company’s name and main address below.'),
          elements: [
            {
              type: 'mui-company-search',
              name: 'companyName',
              title: t('signup:fields.companyName', 'Company name'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.companyName', 'Company name'),
                number: 1
              }),
              validators: [
                {
                  type: 'text',
                  minLength: 1,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.companyName', 'Company name'),
                    number: 1
                  })
                },
                {
                  type: 'text',
                  maxLength: 160,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.companyName', 'Company name'),
                    number: 160
                  })
                },
                {
                  type: 'expression',
                  expression: 'checkIfCompanyNameExists({companyName})',
                  text: t('signup:validation.companyExists', 'Company already exists, please contact support')
                }
              ],
              titleLocation: 'hidden',
              clearIfInvisible: 'none',
              providers: {
                GB: {
                  provider: 'CompanyHouse'
                }
              }
            },
            {
              type: 'mui-countries',
              name: 'country',
              title: t('signup:fields.country.title', 'Country or region'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.countryIsRequired', 'Country is required'),
              enableIf: '{manualEntering} == true',
              strings: {
                searchPlaceholder: t('signup:fields.country.searchCountries', 'Search Countries'),
                countriesPlaceholder: t('signup:fields.country.selectCountry', 'Select Country')
              }
            },
            {
              type: 'mui-address',
              name: 'address1',
              enableIf:
                '({country} notempty && ({manualEntering} == true) || ({company} notempty && {company.addressLine1} empty)',
              title: t('signup:fields.addressLine1', 'Address line 1'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.addressLine1', 'Address line 1'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 100,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    number: 100
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    number: 2
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.addressLine1', 'Address line 1'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                },
                {
                  type: 'expression',
                  text: '',
                  expression: 'showDetails()'
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'address2',
              parentClassName: 'long-input',
              visibleIf: '{showDetails}',
              enableIf: '{manualEntering} == true || ({company} notempty && {company.addressLine1} empty)',
              title: t('signup:fields.addressLine2', 'Address line 2'),
              validators: [
                {
                  type: 'text',
                  maxLength: 100,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.addressLine2', 'Address line 2'),
                    number: 100
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.addressLine2', 'Address line 2'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'townOrCity',
              visibleIf: '{showDetails}',
              enableIf:
                '{manualEntering} == true || ({company} notempty && {company.city} empty) || ({company} notempty && {company.addressLine1} empty)',
              title: t('signup:fields.city', 'Town or city'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.city', 'Town or city'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 40,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.city', 'Town or city'),
                    number: 40
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.city', 'Town or city'),
                    number: 2
                  })
                },
                {
                  type: 'regex',
                  regex: '^[a-zA-ZÀ-ÿ0-9!.,@#$%^&*( )=+_\\t\\r\\n?:/\'\\-"]+$',
                  text: t('signup:validation.charSet', {
                    defaultValue: '{{field}} should be only characters from the following set characters: {{charSet}}',
                    field: t('signup:fields.city', 'Town or city'),
                    charSet: 'a-z A-Z 0-9 !.,@#$%^&*( )=+_?:\'/"-'
                  })
                }
              ]
            },
            {
              type: 'mui-text',
              name: 'postalCode',
              visibleIf: '{showDetails}',
              enableIf:
                '{manualEntering} == true || ({company} notempty && {company.postalCode} empty) || ({company} notempty && {company.addressLine1} empty)',
              title: t('signup:fields.postalCode', 'Postal code'),
              isRequired: true,
              parentClassName: 'long-input',
              requiredErrorText: t('signup:validation.minLength', {
                defaultValue: '{{field}} should be of minimum {{number}} characters length',
                field: t('signup:fields.postalCode', 'Postal code'),
                number: 2
              }),
              validators: [
                {
                  type: 'text',
                  maxLength: 20,
                  text: t('signup:validation.maxLength', {
                    defaultValue: '{{field}} should be of maximum {{number}} characters length',
                    field: t('signup:fields.postalCode', 'Postal code'),
                    number: 20
                  })
                },
                {
                  type: 'text',
                  minLength: 2,
                  text: t('signup:validation.minLength', {
                    defaultValue: '{{field}} should be of minimum {{number}} characters length',
                    field: t('signup:fields.postalCode', 'Postal code'),
                    number: 2
                  })
                }
              ]
            }
          ]
        },
        {
          type: 'panel',
          title: t('signup:pages.employeeDetails', 'Employee details'),
          description: t(
            'signup:fields.employeeDetailsInfo',
            'The number of employees should include all directly employed office staff including permanent, temporary, part-time and labour-only subcontractors. Bona fide subcontractors should be counted separately.'
          ),
          elements: [
            {
              type: 'mui-text',
              name: 'totalEmployeeCount',
              title: t('signup:fields.numberOfEmployees', 'Number of employees'),
              isRequired: true,
              parentClassName: 'input-with-tooltip',
              tooltip: t(
                'signup:tooltip.numberOfEmployees',
                'The number of employees is the total number of direct employees including, permanent, temporary, part-time and labor only subcontractors. UK Labour Law states a labour-only subcontractor is also considered an employee. This does not include Bona Fide subcontractors. We recommend that you consider the total number of effective employees you may have/use at any one time during a 12-month rolling period.'
              ),
              requiredErrorText: t('signup:validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('signup:fields.numberOfEmployees', 'Number of employees')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 1,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            },
            {
              type: 'mui-text',
              name: 'subcontractorEmployeeCount',
              title: t('signup:fields.numBonaFideSubcontractors', 'Number of bona fide subcontractors'),
              titleForMobile: t('signup:fields.numBonaFideSubcontractorsShort', '# of bona fide subcontractors'),
              isRequired: true,
              parentClassName: 'input-with-tooltip',
              tooltip: t(
                'signup:tooltip.bonaFideSubcontractors',
                'Bona Fide are subcontractors who work with their own tools, insurance etc. And are usually paid by invoice. We recommend that you consider the total number of effective employees you may have/use at ony one time during a 12-month rolling period.'
              ),
              requiredErrorText: t('signup:validation.enterField', {
                defaultValue: 'Please enter {{field}}',
                field: t('signup:fields.numBonaFideSubcontractors', 'Number of bona fide subcontractors')
              }),
              validators: [
                {
                  type: 'numeric',
                  minValue: 0,
                  maxValue: 999999
                }
              ],
              inputType: 'number',
              limitInput: '^[0-9]*$'
            }
          ]
        }
      ],
      stepNumber: 2
    },
    {
      title: t('signup:pages.order', 'Order'),
      stepName: SurveySteps.Order,
      elements: [
        {
          type: 'mui-membership',
          name: 'subscription',
          isRequired: true,
          title: t('signup:fields.certificationTiers', 'Certification tier'),
          description: t(
            'signup:fields.certificationTiersDescription',
            'Select the level of compliance you would like to achieve.'
          ),
          requiredErrorText: t('signup:validation.makeSelection', 'Please make a selection.'),
          choicesByUrl: {
            url:
              '/api/v1/products?Location={packageType}&Country={country}&NumOfEmployees={totalEmployeeCount}&isFoundation=true{subscriptionOptionalParams}',
            valueName: 'id',
            titleName: 'subscriptionTier',
            attachOriginalItems: true,
            path: 'products'
          },
          membershipDetails: [
            {
              type: MembershipTypes.Foundation,
              title: t('signup:foundation.foundationTitle', 'Foundation'),
              iconsSrc: ['/static/images/Foundation.svg'],
              description: t(
                'signup:foundation.foundationDescription',
                'Activate your FREE CHAS Foundation account today.'
              ),
              productId: '',
              price: 'FREE'
            },
            {
              type: MembershipTypes.Contractor,
              title: t('signup:foundation.contractorTitle', 'Contractor'),
              iconsSrc: ['/static/images/Standard.svg', '/static/images/Advanced.svg', '/static/images/Elite.svg'],
              description: t(
                'signup:foundation.contractorDescription',
                'Click here to become a CHAS Accredited Contractor and unlock the full potential of your membership.'
              ),
              productId: '',
              price: '',
              redirectToSignup: true
            },
            {
              type: MembershipTypes.Supplier,
              title: t('signup:foundation.supplierTitle', 'Verified Supplier'),
              iconsSrc: ['/static/images/VerifiedSupplier.svg'],
              description: t(
                'signup:foundation.supplierDescription',
                'Click here to become a CHAS Verified Supplier and unlock the full potential of your membership.'
              ),
              productId: '',
              price: '',
              redirectToSignup: true
            }
          ],
          strings: {
            year: t('signup:subscription.year', '/year*'),
            startingFrom: t('signup:subscription.startingFrom', 'starting from'),
            select: t('signup:subscription.select', 'Select'),
            selected: t('signup:subscription.selected', 'Selected')
          }
        },
        {
          type: 'mui-privacy-consent',
          name: 'privacyConsent',
          titleLocation: 'hidden',
          title: t(
            'signup:privacyConsent.title',
            'By placing this order I confirm that I have read and agree with the {0} and {1}.'
          ),
          links: [
            {
              text: t('signup:privacyConsent.tos', 'Terms of Service'),
              href: 'https://veriforce.com/terms-of-service'
            },
            {
              text: t('signup:privacyConsent.privacyPolicy', 'Privacy Policy'),
              href: 'https://veriforce.com/privacy-policy'
            }
          ]
        },
        {
          type: 'html',
          html: `<div class="sd-element no-vertical-padding"><p class='pricing-explanation'>${t(
            'signup:pricesExcludeVAT',
            '*Prices exclude VAT.'
          )}</p></div>`
        },
        {
          type: 'html',
          html: `<div class="sd-element no-vertical-padding"><p class='pricing-explanation'>${t(
            'signup:priceExplanationGeneral',
            'CHAS uses a dynamic pricing system and prices vary depending on certification level and the number of employees your business has.'
          )}</p></div>`
        }
      ],
      stepNumber: 3
    }
  ],
  calculatedValues: [
    {
      name: 'subscriptionOptionalParams',
      expression: "iif({isSubscriptionDisplayed}, '&ready=true', '')"
    }
  ],
  clearInvisibleValues: 'onHiddenContainer',
  showCompletedPage: false,
  showQuestionNumbers: 'off',
  questionErrorLocation: 'bottom',
  pagePrevText: t('signup:navigation.back', 'Back'),
  pageNextText: t('signup:navigation.next', 'Next'),
  completeText: t('signup:navigation.orderNow', 'Order Now')
});
